import { useForm, SubmitHandler, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import GuestService from "@/app/_services/GuestService";
import { loadGuestSchema } from "./GuestSchema";
import { toast } from "@/app/_utils/toast";
import {
  ColorInput,
  DobInput,
  EmailInput,
  FirstNameInput,
  InstagramInput,
  IntakeInput,
  LastNameInput,
  NotesInput,
  OtherInput,
  PhoneInput,
  StateInput,
} from "./GuestInputs";

export default function GuestForm({
  guest,
  onSaved,
  setDisableSubmitButton,
}: {
  guest: any;
  onSaved: () => void;
  setDisableSubmitButton: (v: boolean) => void;
}) {
  const guestForSchema = {
    ...guest,
    phone: guest.phone?.slice(2),
    instagram: guest.instagram?.slice(1),
  };
  const schema = loadGuestSchema(guestForSchema);

  type FormData = yup.InferType<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: guest.first_name ?? "",
      last_name: guest.last_name ?? "",
      instagram: guest.instagram?.slice(1) ?? "",
      phone: guest.phone?.slice(2) ?? "",
      email: guest.email ?? "",
      color: guest.color ?? "",
      notes: guest.notes ?? "",
      state: guest.state ?? "",
    },
  });

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    saveGuest(data);
  };

  async function saveGuest(fields: any) {
    setDisableSubmitButton(true);
    const { instagram, phone, ...rest } = fields;
    const newGuest = {
      instagram: instagram?.length > 0 ? "@" + instagram : "",
      phone: phone?.length > 0 ? "+1" + phone : "",
      ...rest,
    };
    try {
      await GuestService.updateGuest(newGuest);
      toast.success("Guest updated");
      onSaved();
    } catch (e: any) {
      toast.error("Failed to update guest");
    } finally {
      setDisableSubmitButton(false);
    }
  }

  return (
    <form id="edit-guest-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-3 pb-1 pt-4">
        <FirstNameInput control={control} errors={errors} defaultValue={guest.first_name} />
        <LastNameInput control={control} errors={errors} defaultValue={guest.last_name} />
        <DobInput control={control} errors={errors} defaultValue={guest.date_of_birth} />
        <InstagramInput control={control} errors={errors} defaultValue={guest.instagram?.slice(1)} />
        <IntakeInput control={control} errors={errors} defaultValue={guest.intake_method} />
        {guest.other && <OtherInput control={control} errors={errors} defaultValue={guest.other} />}
        <PhoneInput control={control} errors={errors} defaultValue={guest.phone?.slice(2)} />
        <EmailInput control={control} errors={errors} defaultValue={guest.email} />
        <StateInput control={control} errors={errors} defaultValue={guest.state} />
      </div>
      <div className="grid gap-3 border-t border-border py-3">
        <ColorInput control={control} errors={errors} defaultValue={guest.color} />
        <NotesInput control={control} errors={errors} defaultValue={guest.notes} />
      </div>
    </form>
  );
}
